import React, { useEffect, useState } from "react";
import {
    Checkbox,
    Confirm,
    Button,
    Divider,
    Header,
    List,
    Message,
    Segment,
} from "semantic-ui-react";
import { format } from "date-fns";

import { DateTimeFormat } from "utilities/dist/invoice/constants";
import { loadDestroyedStamps, undoDestroyedStamps } from "./actions";
import { formatStampRange } from "../../common/helpers";
import { useDispatch } from "react-redux";
import { toast } from "react-semantic-toasts";

export const StampAssignmentUndoDestroyContainer = () => {
    const dispatch = useDispatch();
    const [showUndoConfirm, setShowUndoConfirm] = useState(false);
    const [isUndoing, setIsUndoing] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [destroyedStamps, setDestroyedStamps] = useState({
        isLoading: true,
        data: [],
        error: "",
    });
    const reloadDestroyedStamps = () =>
        loadDestroyedStamps()
            .then(data => setDestroyedStamps({ isLoading: false, data }))
            .catch(err => setDestroyedStamps({ isLoading: false, data: [], error: err.message }));

    useEffect(() => {
        reloadDestroyedStamps();
    }, []);

    const handleSubmit = async () => {
        setIsUndoing(true);
        try {
            setShowUndoConfirm(false);
            const items = [];

            if (destroyedStamps.data?.length) {
                destroyedStamps.data.forEach(range => {
                    if (selectedIds.includes(range.id)) {
                        items.push(formatStampRange(range));
                    }
                });
            }
            const result = await dispatch(undoDestroyedStamps(selectedIds));
            const toastType = result.success ? "success" : "warning";
            const toastTitle = result.success ? `Stamps are now available` : "Something went wrong";

            toast({
                time: 15000,
                icon: "ticket",
                type: toastType,
                title: (
                    <h4 data-cy={`undo_destroy_stamps_${toastType}_toast_title`}>{toastTitle}</h4>
                ),
                description: (
                    <div data-cy={`undo_destroy_stamps_${toastType}_toast_description`}>
                        <Message.List>
                            {(result.success
                                ? items
                                : ["Could not undo destroyed stamps from your selection"]
                            ).map(item => (
                                <Message.Item key={item}>{item}</Message.Item>
                            ))}
                        </Message.List>
                    </div>
                ),
            });
            setIsUndoing(false);
            setSelectedIds([]);
            await reloadDestroyedStamps();
        } catch (err) {
            setIsUndoing(false);
        }
    };

    return (
        <>
            <Header
                subheader="Make destroyed stamps available again"
                content="Undo destroy stamps"
                as="h3"
                data-cy="undo_destroy_stamps_header"
            />

            <Confirm
                onConfirm={handleSubmit}
                open={showUndoConfirm}
                header="Undo destroyed stamps?"
                onCancel={() => setShowUndoConfirm(false)}
                content="The selected stamps will be available for using in invoices again"
                confirmButton="Confirm"
                cancelButton="Nevermind"
                data-cy="undo_destroy_stamps_confirm"
            />

            <Segment vertical loading={isUndoing || destroyedStamps.isLoading}>
                {!!destroyedStamps.data.length && (
                    <List relaxed="very" divided>
                        {destroyedStamps.data.map((invoiceStamp, index) => {
                            return (
                                <List.Item key={`price_tier_${invoiceStamp.id}`}>
                                    <List.Content floated="left">
                                        <Checkbox
                                            data-cy={`undo_destroy_stamps_${index + 1}`}
                                            onChange={() => {
                                                if (selectedIds.includes(invoiceStamp.id)) {
                                                    setSelectedIds(
                                                        selectedIds.filter(
                                                            id => id !== invoiceStamp.id
                                                        )
                                                    );
                                                } else {
                                                    setSelectedIds([
                                                        ...selectedIds,
                                                        invoiceStamp.id,
                                                    ]);
                                                }
                                            }}
                                        />
                                    </List.Content>
                                    <List.Content floated="left">
                                        <List.Header>{formatStampRange(invoiceStamp)}</List.Header>
                                        <List.Description>
                                            Destroyed at:{" "}
                                            {format(
                                                new Date(invoiceStamp.logged_at),
                                                DateTimeFormat
                                            )}
                                        </List.Description>
                                    </List.Content>
                                </List.Item>
                            );
                        })}
                    </List>
                )}
                <Divider />
                <Button
                    color="yellow"
                    disabled={!selectedIds?.length}
                    onClick={() => setShowUndoConfirm(true)}
                    data-cy="undo_destroy_stamps_button"
                >
                    Undo and make available
                </Button>
                {!!destroyedStamps.error && (
                    <Message
                        error
                        icon="warning sign"
                        header="Something went wrong!"
                        content={destroyedStamps.error}
                    />
                )}
            </Segment>
        </>
    );
};
